import { useEffect, useRef } from 'react';
import { motion, useMotionValue, useTransform, animate, useInView } from 'framer-motion';
import './Cards.scss';

// Assets
import cardOne from '../../assets/Images/Cards/one.svg';
import cardTwo from '../../assets/Images/Cards/two.svg';
import cardThree from '../../assets/Images/Cards/three.svg';
import cardFour from '../../assets/Images/Cards/four.svg';
import cardFive from '../../assets/Images/Cards/five.svg';
import cardSix from '../../assets/Images/Cards/six.svg';
import cardGradient from '../../assets/Images/who-we-are/gradient-bg.svg';

const CARDS_DATA = [
  { imageUrl: cardOne, title: "15+", subtitle: "Years of Domain Expertise" },
  { imageUrl: cardTwo, title: "200+", subtitle: "Projects Delivered" },
  { imageUrl: cardThree, title: "100+", subtitle: "Expert Engineers" },
  { imageUrl: cardFour, title: "24/7", subtitle: "Global Delivery Support" },
  { imageUrl: cardFive, title: "OWASP", subtitle: "Compliant Secure Dev" },
  { imageUrl: cardSix, title: "60%", subtitle: "Faster Time-to-Market" },
];

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.6,
      ease: 'easeOut',
    },
  }),
};

// Sub-component to handle high-performance number counting and text fading
const AnimatedStatValue = ({ text }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  
  // Regex to check if the string starts with a number and separate the suffix
  // e.g., "200+" -> match[1] = "200", match[2] = "+"
  const match = text.match(/^(\d+)(.*)$/);

  // Framer Motion values for 60fps hardware-accelerated counting
  const count = useMotionValue(0);
  const rounded = useTransform(count, Math.round);

  useEffect(() => {
    // If it's a number and it comes into view, animate the count
    if (isInView && match) {
      const target = parseInt(match[1], 10);
      animate(count, target, { duration: 2, ease: 'easeOut' });
    }
  }, [isInView, count, match]);

  // If the text has no numbers (e.g., "OWASP"), apply the fade in-out effect
  if (!match) {
    return (
      <motion.h3
        className="hc-stat-value"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: [0, 1, 0.4, 1] }} // Fades in, dips out slightly, comes back
        transition={{ duration: 2.5, ease: 'easeInOut' }}
        viewport={{ once: true, margin: '-50px' }}
      >
        {text}
      </motion.h3>
    );
  }

  // If it is a number, render the animated value + its suffix
  return (
    <h3 className="hc-stat-value" ref={ref}>
      <motion.span>{rounded}</motion.span>
      <span>{match[2]}</span>
    </h3>
  );
};

export default function Cards() {
  return (
    <section className="hc-cards-section">
      {/* Background glow ellipse */}
      <div className="hc-cards-bg-glow">
        <img src={cardGradient} alt="" aria-hidden="true" />
      </div>

      <div className="hc-stats-grid">
        {CARDS_DATA.map((card, index) => (
          <motion.div
            key={index}
            className="hc-stat-card"
            custom={index}
            variants={cardVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
          >
            <div className="hc-stat-icon">
              <img src={card.imageUrl} alt={card.subtitle} />
            </div>
            
            {/* Inject our smart counter here */}
            <AnimatedStatValue text={card.title} />
            
            <p className="hc-stat-label">{card.subtitle}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}